import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ref, onValue, update } from 'firebase/database';
import { db } from '../firebaseConfig';
import Navbar from '../components/Navbar';
import { exportUsersCsv, exportRidesCsv, exportBookingsCsv, exportPaymentsCsv } from '../csvExportHelper';

const ADMIN_PHONE = '+917017138355';

export default function AdminPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [reviewQueue, setReviewQueue] = useState([]);
  const [viewingPhoto, setViewingPhoto] = useState(null);

  const [grievances, setGrievances] = useState([]);
  const [respondingTo, setRespondingTo] = useState(null);
  const [responseText, setResponseText] = useState('');

  const [exportRange, setExportRange] = useState('all');
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const savedUser = localStorage.getItem('guningo_user');
    if (!savedUser) {
      navigate('/');
      return;
    }
    setUser(JSON.parse(savedUser));
  }, [navigate]);

  const isAdmin = user && user.phone === ADMIN_PHONE;

  useEffect(() => {
    if (!isAdmin) return;
    const usersRef = ref(db, 'users/');
    const unsubscribe = onValue(usersRef, (snapshot) => {
      const data = snapshot.val();
      if (!data) {
        setReviewQueue([]);
        return;
      }
      const queue = [];
      const docTypes = ['license', 'aadhaar', 'rc'];
      const labelMap = { license: 'Driving License', aadhaar: 'Aadhaar Card', rc: 'Vehicle RC' };
      Object.keys(data).forEach(userId => {
        const u = data[userId];
        docTypes.forEach(docType => {
          if (u[docType + 'Status'] === 'needs_review' || u[docType + 'Status'] === 'pending') {
            queue.push({
              id: userId + '_' + docType, userId, docType, label: labelMap[docType],
              photo: u[docType + 'Photo'], reason: u[docType + 'Reason'] || 'Pending review',
              extractedNumber: u[docType + 'ExtractedNumber'] || 'Not found',
              userName: u.name || 'Unknown', userPhone: u.phone || 'Unknown',
            });
          }
        });
      });
      setReviewQueue(queue);
    });
    return () => unsubscribe();
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin) return;
    const feedbackRef = ref(db, 'feedback/');
    const unsubscribe = onValue(feedbackRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const list = Object.keys(data)
          .map(key => ({ id: key, ...data[key] }))
          .sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
        setGrievances(list);
      } else {
        setGrievances([]);
      }
    });
    return () => unsubscribe();
  }, [isAdmin]);

  const approveDocument = async (item) => {
    try {
      await update(ref(db, 'users/' + item.userId), { [item.docType + 'Status']: 'verified' });
      alert('✅ ' + item.label + ' for ' + item.userName + ' has been verified.');
    } catch (error) {
      alert('Error: ' + error.message);
    }
  };

  const rejectDocument = async (item) => {
    if (!window.confirm('Reject ' + item.label + ' for ' + item.userName + '? They will need to re-upload.')) return;
    try {
      await update(ref(db, 'users/' + item.userId), { [item.docType + 'Status']: 'rejected', [item.docType + 'Photo']: null });
      alert('❌ Document rejected.');
    } catch (error) {
      alert('Error: ' + error.message);
    }
  };

  const markResolved = async (item) => {
    try {
      await update(ref(db, 'feedback/' + item.id), { status: 'resolved', resolvedAt: new Date().toISOString() });
      alert('✅ Marked as resolved');
    } catch (error) {
      alert('Error: ' + error.message);
    }
  };

  const sendResponse = async (item) => {
    if (!responseText.trim()) {
      alert('Please write a response before sending.');
      return;
    }
    try {
      await update(ref(db, 'feedback/' + item.id), {
        adminResponse: responseText.trim(),
        respondedAt: new Date().toISOString(),
        status: 'resolved',
      });
      setResponseText('');
      setRespondingTo(null);
      alert('✅ Response sent — the user will see your reply.');
    } catch (error) {
      alert('Error: ' + error.message);
    }
  };

  const handleExport = async (exportFn) => {
    setExporting(true);
    try {
      await exportFn(exportRange);
    } catch (error) {
      alert('Export failed: ' + error.message);
    }
    setExporting(false);
  };

  if (!user) return null;

  if (!isAdmin) {
    return (
      <div style={styles.page}>
        <Navbar userName={user.name} userPhone={user.phone} />
        <div style={styles.deniedWrap}>
          <div style={{ fontSize: 50, marginBottom: 14 }}>🔒</div>
          <h2 style={{ color: '#1a1a2e' }}>Access Denied</h2>
          <p style={{ color: '#9499b5' }}>This section is only available to administrators.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <Navbar userName={user.name} userPhone={user.phone} />
      <div style={styles.container}>

        <h2 style={styles.pageTitle}>🛡️ Admin Panel</h2>
        <p style={styles.pageSub}>Document review queue</p>

        <div style={styles.card}>
          <h3 style={styles.cardTitle}>📊 Export Data</h3>

          <div style={styles.rangeRow}>
            <button style={exportRange === '30days' ? styles.rangeChipActive : styles.rangeChip} onClick={() => setExportRange('30days')}>Last 30 days</button>
            <button style={exportRange === '3months' ? styles.rangeChipActive : styles.rangeChip} onClick={() => setExportRange('3months')}>Last 3 months</button>
            <button style={exportRange === 'all' ? styles.rangeChipActive : styles.rangeChip} onClick={() => setExportRange('all')}>All time</button>
          </div>

          <button style={styles.exportBtn} onClick={() => handleExport(exportUsersCsv)} disabled={exporting}>👤 Export Users</button>
          <button style={styles.exportBtn} onClick={() => handleExport(exportRidesCsv)} disabled={exporting}>🚗 Export Rides</button>
          <button style={styles.exportBtn} onClick={() => handleExport(exportBookingsCsv)} disabled={exporting}>🎫 Export Bookings</button>
          <button style={styles.exportBtn} onClick={() => handleExport(exportPaymentsCsv)} disabled={exporting}>💰 Export Payments</button>

          {exporting && <p style={{ textAlign: 'center', color: '#9499b5', fontSize: 12, marginTop: 8 }}>Preparing download...</p>}
        </div>

        <h3 style={{ marginBottom: 12, color: '#1a1a2e' }}>Needs review ({reviewQueue.length})</h3>

        {reviewQueue.length === 0 ? (
          <div style={styles.emptyState}>
            <div style={{ fontSize: 40, marginBottom: 10 }}>✅</div>
            <h3 style={{ margin: '0 0 4px', color: '#1a1a2e' }}>All caught up</h3>
            <p style={{ color: '#9499b5', margin: 0 }}>No documents currently need review</p>
          </div>
        ) : (
          <div style={styles.reviewGrid}>
            {reviewQueue.map(item => (
              <div key={item.id} style={styles.reviewCard}>
                <div style={{ marginBottom: 10 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, color: '#1a1a2e' }}>{item.label}</div>
                  <div style={{ fontSize: 11, color: '#5a5f7d' }}>{item.userName} · {item.userPhone}</div>
                </div>
                {item.photo && (
                  <img src={item.photo} alt={item.label} style={styles.reviewPhoto} onClick={() => setViewingPhoto(item.photo)} />
                )}
                <div style={styles.reasonBox}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: '#8a6416', marginBottom: 2 }}>Flagged reason:</div>
                  <div style={{ fontSize: 11, color: '#8a6416' }}>{item.reason}</div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 12 }}>
                  <span style={{ color: '#5a5f7d' }}>Extracted number:</span>
                  <b style={{ color: '#1a1a2e' }}>{item.extractedNumber}</b>
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button style={styles.rejectBtn} onClick={() => rejectDocument(item)}>✕ Reject</button>
                  <button style={styles.approveBtn} onClick={() => approveDocument(item)}>✓ Approve</button>
                </div>
              </div>
            ))}
          </div>
        )}

        <h3 style={{ marginBottom: 12, marginTop: 30, color: '#1a1a2e' }}>
          Grievances ({grievances.filter(g => g.status !== 'resolved').length} open)
        </h3>

        {grievances.length === 0 ? (
          <div style={styles.emptyState}>
            <div style={{ fontSize: 40, marginBottom: 10 }}>💬</div>
            <h3 style={{ margin: 0, color: '#1a1a2e' }}>No grievances yet</h3>
          </div>
        ) : (
          <div style={styles.reviewGrid}>
            {grievances.map(item => (
              <div key={item.id} style={styles.reviewCard}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14, color: '#1a1a2e' }}>{item.category}</div>
                    <div style={{ fontSize: 11, color: '#5a5f7d' }}>{item.fromName} · {item.fromPhone}</div>
                  </div>
                  <span style={item.status === 'resolved' ? styles.statusBadgeResolved : styles.statusBadgeOpen}>
                    {item.status === 'resolved' ? '✓ Resolved' : '⏳ Open'}
                  </span>
                </div>

                <p style={{ fontSize: 12, color: '#5a5f7d', marginBottom: 10, lineHeight: 1.5 }}>{item.message}</p>

                {item.adminResponse && (
                  <div style={styles.responseBox}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: '#166534', marginBottom: 2 }}>Your response:</div>
                    <div style={{ fontSize: 12, color: '#166534' }}>{item.adminResponse}</div>
                  </div>
                )}

                {item.status !== 'resolved' && (
                  respondingTo === item.id ? (
                    <div>
                      <textarea
                        style={styles.respondInput}
                        placeholder="Write your response..."
                        value={responseText}
                        onChange={e => setResponseText(e.target.value)}
                      />
                      <div style={{ display: 'flex', gap: 10 }}>
                        <button style={styles.cancelRespondBtn} onClick={() => { setRespondingTo(null); setResponseText(''); }}>Cancel</button>
                        <button style={styles.sendResponseBtn} onClick={() => sendResponse(item)}>Send Response</button>
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', gap: 10 }}>
                      <button style={styles.rejectBtn} onClick={() => markResolved(item)}>✓ Mark Resolved</button>
                      <button style={styles.approveBtn} onClick={() => setRespondingTo(item.id)}>💬 Respond</button>
                    </div>
                  )
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {viewingPhoto && (
        <div style={styles.photoModal} onClick={() => setViewingPhoto(null)}>
          <img src={viewingPhoto} alt="Document" style={styles.photoModalImg} />
        </div>
      )}
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', background: '#ffffff', fontFamily: 'Arial, sans-serif' },
  container: { maxWidth: 900, margin: '0 auto', padding: '30px 20px 40px' },
  pageTitle: { color: '#1a1a2e', margin: '0 0 4px' },
  pageSub: { color: '#9499b5', fontSize: 13, marginBottom: 24 },
  card: { border: '1px solid #eef1f8', borderRadius: 14, padding: 20, marginBottom: 24 },
  cardTitle: { color: '#1a1a2e', margin: '0 0 14px' },
  rangeRow: { display: 'flex', gap: 8, marginBottom: 14 },
  rangeChip: { flex: 1, height: 34, borderRadius: 8, border: '1px solid #eef1f8', background: '#f7f9fc', color: '#5a5f7d', fontWeight: 600, cursor: 'pointer', fontSize: 12 },
  rangeChipActive: { flex: 1, height: 34, borderRadius: 8, border: '1px solid #3a8fd0', background: '#3a8fd0', color: '#fff', fontWeight: 600, cursor: 'pointer', fontSize: 12 },
  exportBtn: { width: '100%', height: 44, borderRadius: 10, border: 'none', background: '#eaf5fc', color: '#3a8fd0', fontWeight: 700, cursor: 'pointer', fontSize: 13, marginBottom: 8 },
  emptyState: { border: '1px solid #eef1f8', borderRadius: 14, padding: 40, textAlign: 'center' },
  reviewGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 14 },
  reviewCard: { border: '1px solid #eef1f8', borderRadius: 14, padding: 14 },
  reviewPhoto: { width: '100%', height: 160, borderRadius: 10, marginBottom: 10, objectFit: 'cover', cursor: 'pointer', background: '#f7f9fc' },
  reasonBox: { background: '#fdf3e3', borderRadius: 8, padding: 10, marginBottom: 8 },
  approveBtn: { flex: 1, height: 38, borderRadius: 8, border: 'none', background: '#00c896', color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: 13 },
  rejectBtn: { flex: 1, height: 38, borderRadius: 8, border: '1.5px solid #e94560', background: '#fff', color: '#e94560', fontWeight: 700, cursor: 'pointer', fontSize: 13 },
  statusBadgeOpen: { background: '#fdf3e3', color: '#8a6416', fontSize: 10, fontWeight: 700, padding: '4px 8px', borderRadius: 10, height: 'fit-content' },
  statusBadgeResolved: { background: '#eaf4ea', color: '#166534', fontSize: 10, fontWeight: 700, padding: '4px 8px', borderRadius: 10, height: 'fit-content' },
  responseBox: { background: '#eaf4ea', borderRadius: 8, padding: 10, marginBottom: 10 },
  respondInput: { width: '100%', minHeight: 70, borderRadius: 8, border: '1px solid #eef1f8', padding: 10, fontSize: 12, marginBottom: 8, fontFamily: 'inherit', boxSizing: 'border-box' },
  cancelRespondBtn: { flex: 1, height: 38, borderRadius: 8, border: '1.5px solid #9499b5', background: '#fff', color: '#5a5f7d', fontWeight: 700, cursor: 'pointer', fontSize: 12 },
  sendResponseBtn: { flex: 1, height: 38, borderRadius: 8, border: 'none', background: '#3a8fd0', color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: 12 },
  deniedWrap: { textAlign: 'center', padding: '100px 20px' },
  photoModal: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, zIndex: 2000, cursor: 'pointer' },
  photoModalImg: { maxWidth: '90%', maxHeight: '90%', borderRadius: 10 },
};